// src/shared/components/ui/Dialog/SenderDetailsDialog.tsx
import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { UserAvatar } from "../Avatar/UserAvatar";
import { X } from "lucide-react";
import { useTranslation } from "../../../hooks/useTranslation";
import { colors } from "../../../styles/theme/colors";
import theme, { DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS, CURRENCY_SYMBOL } from "../../../styles/theme/theme";
import { parseUTCDateTime } from "../../../utils/formatUtils";
import CustomButton from "../Button/Custombutton";
import { useLocalization } from "../../../contexts/LocalizationContext";
import { DialogWrapper } from "./DialogWrapper";
import fromSvg from "../../../../assets/from.svg";
import toSvg from "../../../../assets/to.svg";
import emptypkg from "../../../../assets/emptypkg.svg";
import offer from "../../../../assets/offer.svg";
import planeSvg from "../../../../assets/airplane.svg";
import categorySvg from "../../../../assets/category.svg";
import timeSvg from "../../../../assets/time.svg";
import helpIcon from "../../../../assets/Help icon.svg";
import { Tooltip } from "../Tooltip/Tooltip";
import { SenderPricingDialog } from "./SenderPricingDialog";
import { TravelerPricingDialog } from "./TravelerPricingDialog";
import { useAuth0 } from "@auth0/auth0-react";
import { useGetShipmentDetailsQuery } from "../../../../api/senderApi2";
import { useGetItineraryDetailsQuery } from "../../../../api/travellerApi3";
import {
  calculatePricePerLb,
  calculateBasePrice,
  calculatePlatformFee,
  usePricingConstants,
} from "../../../utils/pricingUtils";
import { useResponsive, dialogLayout } from "../../../utils/responsive";

type UserRole = "sender" | "traveller";

interface SenderDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  sender: {
    id?: number;
    itineraryId?: number;
    shipmentId?: number;
    name?: string;
    profileImageUrl?: string;
    rating?: number;
    origin?: string;
    departure_airport?: string;
    departure_airport_code?: string;
    departure_country_code?: string;
    from?: string;
    destination?: string;
    destination_airport?: string;
    destination_airport_code?: string;
    destination_country_code?: string;
    to?: string;
    packageWeight?: string;
    weight?: number;
    totalWeightLimit?: number;
    currentItemWeight?: number;
    packageCategory?: string;
    category?: string;
    categories?: string[];
    offeredPrice?: string;
    price?: string | number;
    allocatedPrice?: number;
    departure_date?: string;
    departure_time?: string;
    arrival_date?: string;
    shipping_start_date?: string;
    shipping_end_date?: string;
    shipping_time?: string;
    items?: unknown[];
    images?: unknown[];
    currencyCode?: string;
    currencySymbol?: string;
    description?: string;
    platform_fee?: number;
    senderPlatformFeePercentage?: number;
    travelerPlatformFeePercentage?: number;
  } | null;
  itineraryId?: number;
  mode?: UserRole;
  isLoading?: boolean;
  isAccepting?: boolean;
  hasCategoryMismatch?: boolean;
}


const SenderDetailsDialog: React.FC<SenderDetailsDialogProps> = ({
  open,
  onClose,
  onAccept,
  onReject,
  sender,
  itineraryId,
  mode = "sender",
  isLoading = false,
  isAccepting = false,
  hasCategoryMismatch = false,
}) => {
  const [mismatchTooltipOpen, setMismatchTooltipOpen] = React.useState(false);
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth0();
  const { formatWeight } = useLocalization();
  const { percentage: platformPercentage } = usePricingConstants();
  const [priceCalcOpen, setPriceCalcOpen] = useState(false);
  const { isSmallMobile, isMobile } = useResponsive();

  const isViewingTravelerDetails = mode === "traveller";

  // Always fetch shipment details (for sender weight)
  const { data: shipmentDetailsData } = useGetShipmentDetailsQuery(
    String(sender?.shipmentId || ''),
    { enabled: !!sender?.shipmentId && open }
  );

  // Always fetch itinerary details (for traveler weight/price)
  const { data: itineraryDetailsData } = useGetItineraryDetailsQuery(
    String(sender?.itineraryId || ''),
    { enabled: !!sender?.itineraryId && open }
  );

  // Sender weight always comes from shipment (fall back to sender.weight only when viewing the sender's own shipment)
  const senderWeight = Number(
    shipmentDetailsData?.data?.total_weight ?? (isViewingTravelerDetails ? 0 : sender?.weight) ?? 0
  );

  // Traveler weight and price come from itinerary (fall back to sender.weight/price only when viewing the traveler's own itinerary)
  const travelerWeight = parseFloat(
    itineraryDetailsData?.weight ?? (isViewingTravelerDetails ? sender?.weight : undefined) ?? '0'
  );
  const travelerPriceUSD = parseFloat(
    itineraryDetailsData?.price ?? (isViewingTravelerDetails ? sender?.price : undefined) ?? '0'
  );

  // Shipment total price (USD) — never fall back to the traveler's own price when viewing the traveler's offer
  const shipmentPriceUSD = Number(
    shipmentDetailsData?.data?.total_price ?? (isViewingTravelerDetails ? 0 : sender?.price) ?? 0
  );

  const formatName = (name: string | null | undefined) => {
    if (!name || typeof name !== "string") {
      return "Unknown User";
    }
    const nameParts = name.trim().split(" ");
    const firstName = nameParts[0] || "Unknown";
    const lastName = nameParts[1] || "";
    return lastName ? `${firstName} ${lastName.charAt(0)}.` : firstName;
  };


  const fromName = sender?.origin || sender?.departure_airport || sender?.from || "";
  const fromIata = sender?.departure_airport_code || "";
  const fromCountryCode = sender?.departure_country_code || "";

  const toName = sender?.destination || sender?.destination_airport || sender?.to || "";
  const toIata = sender?.destination_airport_code || "";
  const toCountryCode = sender?.destination_country_code || "";

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS);
  };

  // Departure date is localized using departure time so the UTC->local conversion
  // resolves to the correct calendar day. Output remains date-only.
  const formatDepartureDate = (dateStr?: string, timeStr?: string) => {
    if (!dateStr) return '';
    const d = parseUTCDateTime(dateStr, timeStr);
    if (!d) return '';
    return d.toLocaleDateString(DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS);
  };

  const departureTime = itineraryDetailsData?.departure_time || sender?.departure_time;
  const formattedDepartureDate = isViewingTravelerDetails
    ? formatDepartureDate(sender?.departure_date, departureTime)
    : formatDate(sender?.shipping_start_date);
  const formattedArrivalDate = isViewingTravelerDetails
    ? formatDate(sender?.arrival_date)
    : formatDate(sender?.shipping_end_date);

  const shippingDays = (() => {
    if (!isViewingTravelerDetails || !sender?.departure_date || !sender?.arrival_date) return null;
    try {
      const dep = new Date(sender.departure_date);
      const arr = new Date(sender.arrival_date);
      if (isNaN(dep.getTime()) || isNaN(arr.getTime())) return '';
      const diff = Math.floor((arr.getTime() - dep.getTime()) / (1000 * 60 * 60 * 24));
      if (diff <= 1) return '1 day';
      return `${diff}-${diff + 1} days`;
    } catch {
      return '';
    }
  })();

  // Always show sender's shipment weight
  const displayWeightLbs = senderWeight;
  const formattedWeight = formatWeight(displayWeightLbs);
  // Weight (and anything derived from it, like the price breakdown) is unavailable until shipment details load
  const isWeightUnavailable = displayWeightLbs === 0;
  // Sender viewing a traveler's offer with no shipment weight loaded yet — block actions that depend on it
  const isTravelerOfferMissingWeight = isViewingTravelerDetails && isWeightUnavailable;

  const category = sender?.packageCategory || sender?.category || (sender?.categories?.length ? sender.categories.join(", ") : "");

  // Mismatch: any shipment item's category_id not found in itinerary's category list
  const hasCategoryMismatchFromData = React.useMemo(() => {
    const shipmentItems: { item_category_id?: string }[] = shipmentDetailsData?.data?.items ?? [];
    const itineraryCategories: { category_id?: string }[] =
      itineraryDetailsData?.categories ?? itineraryDetailsData?.data?.categories ?? [];
    if (!shipmentItems.length || !itineraryCategories.length) return false;
    const itineraryCategoryIds = new Set(itineraryCategories.map((c) => c.category_id));
    return shipmentItems.some((item) => item.item_category_id && !itineraryCategoryIds.has(item.item_category_id));
  }, [shipmentDetailsData, itineraryDetailsData]);

  const resolvedHasCategoryMismatch = hasCategoryMismatch || hasCategoryMismatchFromData;

  // All hooks above — safe to early-return now
  if (isLoading || !sender) {
    return (
      <DialogWrapper
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            minHeight: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {isViewingTravelerDetails ? t('travelerDetails.loading') : t('senderDetails.loading')}
        </Typography>
      </DialogWrapper>
    );
  }

  // Notification payload's fee percentages take priority over the detail-fetch fields (can be null)
  const senderPlatformFeePercentage = sender?.senderPlatformFeePercentage
    ?? shipmentDetailsData?.data?.sender_platform_fee_percentage
    ?? itineraryDetailsData?.sender_platform_fee_percentage;
  const travelerPlatformFeePercentage = sender?.travelerPlatformFeePercentage
    ?? shipmentDetailsData?.data?.traveler_platform_fee_percentage
    ?? itineraryDetailsData?.traveler_platform_fee_percentage;

  // Price calculations using pricingUtils
  let basePriceUSD = 0;
  let platformFeeUSD = 0;

  if (isViewingTravelerDetails) {
    // Sender viewing traveler's offer: pricePerLb from sender's own shipment
    const pricePerLb = calculatePricePerLb(shipmentPriceUSD, senderWeight);
    // base price applied against sender's booked weight
    basePriceUSD = calculateBasePrice(senderWeight, pricePerLb);
    const feePercentage = senderPlatformFeePercentage != null ? Number(senderPlatformFeePercentage) / 100 : platformPercentage;
    platformFeeUSD = calculatePlatformFee(basePriceUSD, feePercentage);
  } else {
    // Traveler viewing sender's shipment: pricePerLb from traveler's itinerary
    const pricePerLb = calculatePricePerLb(travelerPriceUSD, travelerWeight);
    // base price applied against sender's shipment weight
    basePriceUSD = calculateBasePrice(senderWeight, pricePerLb);
    const feePercentage = travelerPlatformFeePercentage != null ? Number(travelerPlatformFeePercentage) / 100 : platformPercentage;
    platformFeeUSD = calculatePlatformFee(basePriceUSD, feePercentage);
  }

  // Pricing popup fee percentages, scoped per dialog rather than per viewer
  const senderPricingDialogFeePercentage = senderPlatformFeePercentage != null
    ? Number(senderPlatformFeePercentage)
    : undefined;
  const travelerPricingDialogFeePercentage = travelerPlatformFeePercentage != null
    ? Number(travelerPlatformFeePercentage)
    : undefined;

  // When the notification already carries the server-calculated allocated price, use it directly
  // instead of recomputing on the frontend. Prices are always USD, no conversion needed.
  const hasAllocatedPriceOverride = sender?.allocatedPrice != null;
  const basePriceUSDFinal = hasAllocatedPriceOverride
    ? Number(sender!.allocatedPrice)
    : basePriceUSD;

  const formattedBasePrice = `${CURRENCY_SYMBOL}${basePriceUSDFinal.toFixed(2)}`;

  const formattedPlatformFee = `${CURRENCY_SYMBOL}${platformFeeUSD.toFixed(2)}`;


  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: dialogLayout.paper,
        style: {
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
        },
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
          isolation: "isolate",
          position: "relative",
          zIndex: 1,
          maxHeight: dialogLayout.maxHeight,
          overflow: "hidden",
          touchAction: "pan-y",
        }}
      >
        {/* Sticky Header: Ripple + Title + User Details */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            flexShrink: 0,
            zIndex: 2,
            backgroundColor: "#fff",
          }}
        >

          {/* Header with title and close button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 0,
              width: "100%",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: dialogLayout.headerPadding,
                gap: dialogLayout.headerGap,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  width: "100%",
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: dialogLayout.titleFontSize,
                  lineHeight: dialogLayout.titleLineHeight,
                  color: "#484A54",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {isViewingTravelerDetails ? t('travelerDetails.title') : t('senderDetails.title')}
              </Typography>
            </Box>

            {/* Close button */}
            <IconButton
              onClick={onClose}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px",
                position: "absolute",
                width: dialogLayout.closeButtonSize,
                height: dialogLayout.closeButtonSize,
                right: dialogLayout.closeButtonOffset,
                top: dialogLayout.closeButtonOffset,
                borderRadius: "8px",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <X size={24} color="#A4A7AE" />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: 0,
                width: "100%",
                height: dialogLayout.headerSpacerHeight,
              }}
            />
          </Box>

          {/* User profile section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: dialogLayout.profilePadding,
              width: "100%",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* User info with avatar and rating */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                padding: 0,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    filter: isAuthenticated ? "none" : "blur(16px)",
                    transition: "filter 0.3s ease",
                    flexShrink: 0,
                  }}
                >
                  <UserAvatar
                    src={sender.profileImageUrl}
                    name={sender.name}
                    userRole={isViewingTravelerDetails ? "traveller" : "sender"}
                    size={isSmallMobile ? dialogLayout.avatarSizeSmall : dialogLayout.avatarSize}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: 0,
                    gap: "4px",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontStyle: "normal",
                      fontWeight: 500,
                      fontSize: dialogLayout.nameFontSize,
                      lineHeight: dialogLayout.nameLineHeight,
                      color: "#373940",
                    }}
                  >
                    {formatName(sender.name)}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: 0,
                      gap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "12px",
                        height: "12px",
                        fontSize: "12px",
                        lineHeight: "12px",
                      }}
                    >
                      ⭐
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontStyle: "normal",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "21px",
                        color: "#667071",
                      }}
                    >
                      {sender.rating ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: dialogLayout.bodyPadding,
            gap: dialogLayout.detailIconInnerSizeSmall,
            width: "100%",
            overflowY: "auto",
            flexGrow: 1,
            minHeight: 0,
            touchAction: "pan-y",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#D9DADF',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#888',
            },
            scrollbarWidth: 'thin',
            scrollbarColor: '#D9DADF transparent',
          }}
        >
          {/* Travel route section with airplane icon */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              padding: 0,
              gap: dialogLayout.innerGap,
              width: "100%",
            }}
          >
            {/* From */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: 0,
                gap: "5px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  gap: "6px",
                  height: dialogLayout.routeIconSize,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: dialogLayout.routeIconSize,
                    height: dialogLayout.routeIconSize,
                    borderRadius: "4999.5px",
                    backgroundColor: colors.traveller.light || "#F2E6EE",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={fromSvg}
                    alt="From"
                    sx={{
                      width: dialogLayout.routeIconInnerSize,
                      height: dialogLayout.routeIconInnerSize,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: dialogLayout.labelFontSize,
                    lineHeight: dialogLayout.labelLineHeight,
                    color: "#838799",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("searchForm.from")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: dialogLayout.locationFontSize,
                  lineHeight: dialogLayout.locationLineHeight,
                  color: "#484A54",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {fromName}
                {fromIata && (
                  <Box
                    component="span"
                    sx={{ whiteSpace: "nowrap", display: { xs: "block", sm: "inline" } }}
                  >
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      {" "}
                    </Box>
                    ({fromIata})
                  </Box>
                )}
              </Typography>
              {formattedDepartureDate && (
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 400,
                    fontSize: dialogLayout.dateFontSize,
                    lineHeight: dialogLayout.dateLineHeight,
                    color: "#000000",
                    mt: "2px",
                  }}
                >
                  {formattedDepartureDate}
                </Typography>
              )}
            </Box>

            {/* Airplane icon and dashed line */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: 0,
                gap: "2px",
                flexShrink: 0,
                minWidth: 0,
                marginTop: dialogLayout.planeMarginTop,
              }}
            >
              <Box
                sx={{
                  width: dialogLayout.routeDashWidth,
                  flexShrink: 1,
                  height: 0,
                  border: `1px dashed ${colors.traveller.primary}`,
                }}
              />
              <Box
                component="img"
                src={planeSvg}
                alt="Airplane"
                sx={{
                  width: dialogLayout.planeIconSize,
                  height: dialogLayout.planeIconSize,
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  width: dialogLayout.routeDashWidth,
                  flexShrink: 1,
                  height: 0,
                  border: `1px dashed ${colors.traveller.primary}`,
                }}
              />
            </Box>

            {/* To */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                padding: 0,
                gap: "5px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  gap: "2px",
                  height: dialogLayout.routeIconSize,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: dialogLayout.routeIconSize,
                    height: dialogLayout.routeIconSize,
                    borderRadius: "4999.5px",
                    backgroundColor: colors.traveller.light || "#F2E6EE",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={toSvg}
                    alt="To"
                    sx={{
                      width: dialogLayout.routeIconInnerSize,
                      height: dialogLayout.routeIconInnerSize,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: dialogLayout.labelFontSize,
                    lineHeight: dialogLayout.labelLineHeight,
                    color: "#838799",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("searchForm.to")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: dialogLayout.locationFontSize,
                  lineHeight: dialogLayout.locationLineHeight,
                  color: "#484A54",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  textAlign: "right",
                }}
              >
                {toName}
                {toIata && (
                  <Box
                    component="span"
                    sx={{ whiteSpace: "nowrap", display: { xs: "block", sm: "inline" } }}
                  >
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      {" "}
                    </Box>
                    ({toIata})
                  </Box>
                )}
              </Typography>
              {formattedArrivalDate && (
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 400,
                    fontSize: dialogLayout.dateFontSize,
                    lineHeight: dialogLayout.dateLineHeight,
                    color: "#000000",
                    mt: "2px",
                    textAlign: "right",
                  }}
                >
                  {formattedArrivalDate}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Package details */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: 0,
              gap: dialogLayout.detailsGap,
              width: "100%",
            }}
          >
            {/* Weight */}
            {!isWeightUnavailable && <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: 0,
                justifyContent: "space-between",
                gap: "8px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  gap: dialogLayout.innerGap,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: dialogLayout.detailIconSize,
                    height: dialogLayout.detailIconSize,
                    borderRadius: "8332.5px",
                    backgroundColor: colors.traveller.light || "#F2E6EE",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={emptypkg}
                    alt="Package"
                    sx={{
                      width: dialogLayout.detailIconInnerSize,
                      height: dialogLayout.detailIconInnerSize,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: dialogLayout.labelFontSize,
                    lineHeight: dialogLayout.labelLineHeight,
                    color: "#667071",
                  }}
                >
                  {isViewingTravelerDetails ? t('travelerDetails.packageWeight') : t('senderDetails.packageWeight')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 500,
                  fontSize: dialogLayout.valueFontSize,
                  lineHeight: dialogLayout.valueLineHeight,
                  textAlign: "right",
                  color: "#000000",
                  flexShrink: 0,
                }}
              >
                {formattedWeight.display}
              </Typography>
            </Box>}

            {/* Package Category */}
            {category && !isViewingTravelerDetails && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  justifyContent: "space-between",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 0,
                    gap: dialogLayout.innerGap,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: dialogLayout.detailIconSize,
                      height: dialogLayout.detailIconSize,
                      borderRadius: "8332.5px",
                      backgroundColor: colors.traveller.light || "#F2E6EE",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        width: "20px",
                        height: "20px",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M17.5 5.83333L10 1.66666L2.5 5.83333V14.1667L10 18.3333L17.5 14.1667V5.83333Z"
                          stroke={colors.traveller.primary}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2.91669 6.25L10 10L17.0834 6.25"
                          stroke={colors.traveller.primary}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 18.3333V10"
                          stroke={colors.traveller.primary}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: dialogLayout.labelFontSize,
                        lineHeight: dialogLayout.labelLineHeight,
                        color: "#667071",
                      }}
                    >
                      {t('senderDetails.packageCategory')}
                    </Typography>
                    {resolvedHasCategoryMismatch && (
                      <Tooltip
                        message="tooltips.categoryMismatch"
                        placement="top"
                        maxWidth={220}
                        trigger="click"
                        open={mismatchTooltipOpen}
                        onOpen={() => setMismatchTooltipOpen(true)}
                        onClose={() => setMismatchTooltipOpen(false)}
                      >
                        <Box
                          component="img"
                          src={helpIcon}
                          alt="Category mismatch warning"
                          sx={{ width: "16px", height: "16px", flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: dialogLayout.valueFontSize,
                    lineHeight: dialogLayout.valueLineHeight,
                    textAlign: "right",
                    color: "#000000",
                    flexShrink: 0,
                    maxWidth: "45%",
                    wordBreak: "break-word",
                  }}
                >
                  {category}
                </Typography>
              </Box>
            )}

            {/* Price */}
            {(hasAllocatedPriceOverride || basePriceUSD !== 0) && <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: 0,
                justifyContent: "space-between",
                gap: "8px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  gap: dialogLayout.innerGap,
                  minWidth: 0,
                  flexShrink: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: dialogLayout.detailIconSize,
                    height: dialogLayout.detailIconSize,
                    borderRadius: "8332.5px",
                    backgroundColor: colors.traveller.light || "#F2E6EE",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={offer}
                    alt="Price"
                    sx={{
                      width: dialogLayout.detailIconInnerSize,
                      height: dialogLayout.detailIconInnerSize,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: dialogLayout.labelFontSize,
                    lineHeight: dialogLayout.labelLineHeight,
                    color: "#667071",
                  }}
                >
                  {isViewingTravelerDetails ? t('travelerDetails.offeredPrice') : t('senderDetails.offeredPrice')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "2px",
                  flexShrink: 0,
                  maxWidth: { xs: "45%", sm: "55%" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: dialogLayout.valueFontSize,
                    lineHeight: dialogLayout.valueLineHeight,
                    textAlign: "right",
                    color: "#000000",
                  }}
                >
                  {formattedBasePrice}
                </Typography>
                {!isTravelerOfferMissingWeight && (
                  <Typography
                    component="button"
                    onClick={() => setPriceCalcOpen(true)}
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontWeight: 400,
                      fontSize: { xs: "11px", sm: "12px" },
                      lineHeight: { xs: "15px", sm: "18px" },
                      color: colors.traveller.primary,
                      textDecoration: "underline",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "right",
                      whiteSpace: "normal",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {isViewingTravelerDetails ? t('travelerDetails.howFinalPriceCalculated') : t('senderDetails.howFinalEarningCalculated')}
                  </Typography>
                )}
              </Box>
            </Box>}

            {/* Shipping Time */}
            {(isViewingTravelerDetails ? !!shippingDays : !!sender.shipping_time) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  justifyContent: "space-between",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 0,
                    gap: dialogLayout.innerGap,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: dialogLayout.detailIconSize,
                      height: dialogLayout.detailIconSize,
                      borderRadius: "8332.5px",
                      backgroundColor: colors.traveller.light || "#F2E6EE",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={timeSvg}
                      alt="Shipping Time"
                      sx={{ width: dialogLayout.detailIconInnerSizeSmall, height: dialogLayout.detailIconInnerSizeSmall }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: theme.typography.fontFamily,
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: dialogLayout.labelFontSize,
                      lineHeight: dialogLayout.labelLineHeight,
                      color: "#667071",
                    }}
                  >
                    {t('senderDetails.shippingTime')}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: dialogLayout.valueFontSize,
                    lineHeight: dialogLayout.valueLineHeight,
                    textAlign: "right",
                    color: "#000000",
                    flexShrink: 0,
                  }}
                >
                  {isViewingTravelerDetails ? shippingDays : sender.shipping_time}
                </Typography>
              </Box>
            )}

            {/* Categories Accepted (traveler mode only) */}
            {isViewingTravelerDetails && sender.categories && sender.categories.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: 0,
                  justifyContent: "space-between",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: 0,
                    gap: dialogLayout.innerGap,
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: dialogLayout.detailIconSize,
                      height: dialogLayout.detailIconSize,
                      borderRadius: "8332.5px",
                      backgroundColor: colors.traveller.light || "#F2E6EE",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={categorySvg}
                      alt="Categories"
                      sx={{ width: dialogLayout.detailIconInnerSizeSmall, height: dialogLayout.detailIconInnerSizeSmall }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: dialogLayout.labelFontSize,
                        lineHeight: dialogLayout.labelLineHeight,
                        color: "#667071",
                        whiteSpace: { xs: "normal", sm: "nowrap" },
                      }}
                    >
                      {t('travelerDetails.categoriesAccepted')}
                    </Typography>
                    {resolvedHasCategoryMismatch && (
                      <Tooltip
                        message="tooltips.categoryMismatch"
                        placement="top"
                        maxWidth={220}
                        trigger="click"
                        open={mismatchTooltipOpen}
                        onOpen={() => setMismatchTooltipOpen(true)}
                        onClose={() => setMismatchTooltipOpen(false)}
                      >
                        <Box
                          component="img"
                          src={helpIcon}
                          alt="Category mismatch warning"
                          sx={{ width: "16px", height: "16px", flexShrink: 0 }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontStyle: "normal",
                    fontWeight: 500,
                    fontSize: dialogLayout.valueFontSize,
                    lineHeight: dialogLayout.valueLineHeight,
                    textAlign: "right",
                    color: "#000000",
                    wordBreak: "break-word",
                    flexShrink: 0,
                    maxWidth: "45%",
                  }}
                >
                  {sender.categories.join(", ")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Description */}
          {sender.description && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: 0,
                gap: "8px",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#181D27",
                }}
              >
                {t('senderDetails.description')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#667071",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {sender.description}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Sticky Actions Footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: dialogLayout.footerPadding,
            gap: dialogLayout.innerGap,
            width: "100%",
            flexShrink: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            borderTop: "1px solid #F2F4F7",
          }}
        >
          <CustomButton
            buttonText={t('senderDetails.reject')}
            showButtonText
            onClick={onReject}
            disabled={isAccepting || isTravelerOfferMissingWeight}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: dialogLayout.buttonPadding,
              gap: "8px",
              flex: 1,
              height: dialogLayout.buttonHeight,
              background: colors.action.reject,
              borderRadius: "8px",
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: dialogLayout.buttonFontSize,
              lineHeight: dialogLayout.buttonLineHeight,
              color: colors.neutral.white,
              textTransform: "none",
              "&:hover": {
                background: "#DC2626",
                opacity: 0.9,
              },
            }}
          />
          <CustomButton
            buttonText={isAccepting ? t('common.loading') : t('senderDetails.accept')}
            showButtonText
            onClick={onAccept}
            disabled={isAccepting || isTravelerOfferMissingWeight}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              padding: dialogLayout.buttonPadding,
              gap: "8px",
              flex: 1,
              height: dialogLayout.buttonHeight,
              background: colors.semantic.success,
              borderRadius: "8px",
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: dialogLayout.buttonFontSize,
              lineHeight: dialogLayout.buttonLineHeight,
              color: colors.neutral.white,
              textTransform: "none",
              "&:hover": {
                background: "#059669",
                opacity: 0.9,
              },
            }}
          />
        </Box>
      </Box>

      {isViewingTravelerDetails ? (
        <SenderPricingDialog
          open={priceCalcOpen}
          onClose={() => setPriceCalcOpen(false)}
          originCode={fromCountryCode}
          destinationCode={toCountryCode}
          senderWeight={senderWeight}
          travelerPrice={shipmentPriceUSD}
          travelerWeight={senderWeight}
          feePercentage={senderPricingDialogFeePercentage}
          isFromNotification
        />
      ) : (
        <TravelerPricingDialog
          open={priceCalcOpen}
          onClose={() => setPriceCalcOpen(false)}
          originCode={fromCountryCode}
          destinationCode={toCountryCode}
          senderWeight={senderWeight}
          travelerPrice={travelerPriceUSD}
          travelerWeight={travelerWeight}
          feePercentage={travelerPricingDialogFeePercentage}
          isFromNotification
        />
      )}
    </DialogWrapper>
  );
};

export default SenderDetailsDialog;
